import { useState } from "react";
import { Users, Edit, Phone, ChevronDown, ChevronRight, Briefcase, Building2, Search, X, HeartPulse } from "lucide-react";
import { formatCurrency as formatCurrencyBase, phoneHref } from "../../lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { fuzzyMatch } from "../../lib/searchUtils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";

const EmployeeList = ({
  activeEmployees,
  inactiveEmployees,
  inactiveOpen,
  setInactiveOpen,
  onEdit,
}) => {
  const [search, setSearch] = useState("");

  const matchEmployee = (emp) => {
    if (!search.trim()) return true;
    const searchable = [emp.name, emp.role, emp.phone, emp.email, emp.hire_date, emp.emergency_contact?.name].filter(Boolean).join(" ");
    return fuzzyMatch(search, searchable);
  };

  const filteredActive = activeEmployees.filter(matchEmployee);
  const filteredInactive = inactiveEmployees.filter(matchEmployee);

  const formatCurrency = (amount) => formatCurrencyBase(amount, true);



  const CallLink = ({ phone, label, testId, emergency = false }) => (
    <a
      href={phoneHref(phone)}
      title={label}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-sm transition-colors ${
        emergency ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`}
      data-testid={testId}
    >
      {emergency ? <HeartPulse className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
      <span className="font-mono">{phone}</span>
    </a>
  );

  const getEmployeeType = (employee) => {
    const types = [];
    if (employee.is_field) types.push("Field");
    if (employee.is_office) types.push("Office");
    return types.length > 0 ? types.join(" / ") : "-";
  };

  const renderEmployeeRow = (employee) => (
    <TableRow
      key={employee.id}
      className="table-row"
      data-testid={`employee-row-${employee.id}`}
    >
      <TableCell className="font-medium">{employee.name}</TableCell>
      <TableCell>{employee.role}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {employee.is_field && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
              <Briefcase className="w-3 h-3" /> Field
            </span>
          )}
          {employee.is_office && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
              <Building2 className="w-3 h-3" /> Office
            </span>
          )}
          {!employee.is_field && !employee.is_office && (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right font-mono">
        {formatCurrency(employee.hourly_rate)}/hr
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1.5 items-start">
          {employee.phone && (
            <CallLink phone={employee.phone} label={`Call ${employee.name}`} testId={`call-employee-${employee.id}`} />
          )}
          {employee.emergency_contact?.phone && (
            <div className="flex flex-col" data-testid={`emergency-contact-${employee.id}`}>
              <CallLink
                phone={employee.emergency_contact.phone}
                label={`Call emergency contact ${employee.emergency_contact.name || ""}`}
                testId={`call-emergency-${employee.id}`}
                emergency
              />
              <span className="text-[11px] text-muted-foreground pl-1">
                Emergency: {employee.emergency_contact.name || "Contact"}
                {employee.emergency_contact.relationship ? ` (${employee.emergency_contact.relationship})` : ""}
              </span>
            </div>
          )}
          {!employee.phone && !employee.emergency_contact?.phone && <span className="text-muted-foreground">-</span>}
        </div>
      </TableCell>
      <TableCell>{employee.hire_date || "-"}</TableCell>
      {!employee.is_active && (
        <>
          <TableCell>{employee.separation_date || "-"}</TableCell>
          <TableCell>
            {employee.rehireable === true && <span className="text-green-600 font-medium">Yes</span>}
            {employee.rehireable === false && <span className="text-red-600 font-medium">No</span>}
            {employee.rehireable === null && "-"}
          </TableCell>
        </>
      )}
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(employee)}
            data-testid={`edit-employee-${employee.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search employees..."
          className="pl-9 pr-9"
          data-testid="employee-search-input"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Active Employees */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Active Employees ({filteredActive.length}{search ? ` of ${activeEmployees.length}` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredActive.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Hourly Rate</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Hire Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActive.map(renderEmployeeRow)}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">No active employees</p>
          )}
        </CardContent>
      </Card>

      {/* Inactive Employees - Collapsible */}
      {filteredInactive.length > 0 && (
        <Collapsible open={inactiveOpen} onOpenChange={setInactiveOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Users className="w-5 h-5" />
                    Inactive Employees ({filteredInactive.length}{search ? ` of ${inactiveEmployees.length}` : ''})
                  </span>
                  {inactiveOpen ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  )}
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="p-0 pt-0">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Hourly Rate</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Hire Date</TableHead>
                      <TableHead>Separation Date</TableHead>
                      <TableHead>Re-Hireable</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInactive.map(renderEmployeeRow)}
                  </TableBody>
                </Table>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
    </div>
  );
};

export default EmployeeList;
